import { useResendEmailConfirmationMutation, useUser } from '@lopo-tech/lopo-api'
import React, { useCallback, useEffect, useState } from 'react'
import { Button } from 'src/components/button/button'
import { SvgIcon } from 'src/components/icons/svg-icon'
import { ProgressIndicator } from 'src/components/progress_indicator/progress_indicator'
import { CLASS_PREFIX } from 'src/constants'
import { textResources } from 'src/lang/de'
import { UrlUtils } from 'src/utils/url/url'
import { AUTH_CLS } from '../../auth/constants'
import './email_verification.scss'

type IProps = {
  onNext: () => void
  email: string
  emailRedirectUrl?: string
}

const cls = `${CLASS_PREFIX}email-verification`
const POLL_INTERVAL = 5000

export const EmailVerification: React.FC<IProps> = ({ onNext, email, emailRedirectUrl }) => {
  const [emailSent, setEmailSent] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [resendEmailConfirmation] = useResendEmailConfirmationMutation()
  const { data, startPolling, stopPolling } = useUser()

  const isConfirmed = !!data?.me?.confirmedAt

  useEffect(() => {
    if (!isConfirmed) {
      startPolling(POLL_INTERVAL)
    }
    return () => stopPolling()
  }, [isConfirmed, startPolling, stopPolling])

  useEffect(() => {
    if (isConfirmed) {
      stopPolling()
    }
  }, [isConfirmed, stopPolling])

  const handleResendEmail = useCallback(async () => {
    setErrorMessage('')
    setEmailSent(false)
    try {
      await resendEmailConfirmation({
        variables: { input: { redirectUrl: emailRedirectUrl ?? UrlUtils.emailConfirmationRedirectUrl() } }
      })
      setEmailSent(true)
    } catch {
      setErrorMessage(textResources.shared.errorUnknown)
    }
  }, [resendEmailConfirmation])

  if (isConfirmed) {
    return (
      <div className={cls}>
        <div className={`${cls}__header`}>
          <div />
          <ProgressIndicator steps={3} current={1} />
        </div>
        <div className={`${cls}__content`}>
          <div>
            <div className={`${AUTH_CLS}__header`}>{textResources.authentication.emailConfirmed}</div>
            <div className={`${AUTH_CLS}__subheader`}>{textResources.authentication.emailConfirmedSubtitle}</div>
          </div>
          <div className={`${cls}__success-icon`}>
            <SvgIcon name="General/check" size={48} />
          </div>
          <Button className={`${cls}__continue`} size="large" title={textResources.authentication.continueButton} onClick={onNext} />
        </div>
      </div>
    )
  }

  return (
    <div className={cls}>
      <div className={`${cls}__header`}>
        <div />
        <ProgressIndicator steps={3} current={1} />
      </div>
      <div className={`${cls}__content`}>
        <div>
          <div className={`${AUTH_CLS}__header`}>{textResources.authentication.checkEmailTitle}</div>
          <div className={`${AUTH_CLS}__subheader`}>
            {textResources.authentication.checkEmailSubtitle(email)}
          </div>
        </div>
        {errorMessage && <div className={`${cls}__error`}>{errorMessage}</div>}
        {emailSent && <div className={`${cls}__email-sent`}>{textResources.authentication.resendEmail} ✓</div>}
        <button className={`${cls}__resend-email`} onClick={handleResendEmail}>
          <span className={`${cls}__resend-email__label`}>{textResources.authentication.resendEmail}</span>
        </button>
      </div>
    </div>
  )
}
